import * as R from "ramda";

import stopTypes, { originalTypes } from "../enums/stopTypes";
import { isAddParcelRequest, isAddCollectionRequest } from "./request";
import { joinArrayToString } from "../utils/string";
import { LUNCH_BREAK } from "../constants/strings";
import { createTimeRange } from "../utils/date";

export const formatStopAddress = (stop) => {
  if (isBreakStop(stop)) {
    return LUNCH_BREAK.toUpperCase();
  }
  return joinArrayToString([
    stop.stopAddress.contactName,
    stop.stopAddress.address1,
  ]);
};

const formatRouteStopLabel = (routeStop) => {
  const address = formatStopAddress(routeStop);

  return joinArrayToString(
    [routeStop.displayNumber, routeStop.stopAddress?.postcode, address],
    " - "
  );
};

export const createStopOptions = (stops) =>
  stops.map((routeStop) => ({
    value: routeStop.stopNumber,
    label: formatRouteStopLabel(routeStop),
  }));

export const isBreakStop = (stop) =>
  stop.stopTypeDescription === stopTypes.break;

export const isCollectionStop = (stop) =>
  stop.stopTypeDescription === stopTypes.collection;

export const isShopStop = (stop) => stop.stopTypeDescription === stopTypes.shop;

export const findKingParcel = (parcels = []) =>
  parcels?.find((p) => +p.parcelIndex === 1);

export const findKingParcelPostcode = (stop) =>
  findKingParcel(stop?.parcel)?.address?.postcode;

export const getRemainingParcelCodes = (stops, selectedParcels) =>
  getStopParcelCodes(stops[0]).filter(
    (parcelCode) => !selectedParcels[parcelCode]
  );

export const getSelectedParcelsCount = (parcels) => Object.keys(parcels).length;

export const generateNewStop = (stopTemplate, stops, requests) => {
  const stopNumber = getHighestStopNumber(stops, requests) + 1;
  const sequenceNumber = getHighestSequenceNumber(stops) + 1;

  return {
    ...stopTemplate,
    stopNumber,
    sequenceNumber,
    displayNumber: `${sequenceNumber}`,
  };
};

export const createCollectionStop = ({
  collectionCode,
  collectionType,
  collectionReference,
  serviceStartTime,
  serviceEndTime,
  address,
}) => ({
  ...originalTypes.collection,
  bulk: false,
  consolidated: false,
  collectionIndex: 1,
  eta: "",
  opportunityTime: 0,
  exception: 0,
  collectionCode,
  collectionNumber: collectionReference,
  collectionType,
  serviceStartTime,
  serviceEndTime,
  stopAddress: address,
});

export const makeStopForRequest = (request) => {
  if (isAddParcelRequest(request)) {
    return { ...originalTypes.delivery, parcel: [] };
  }

  if (isAddCollectionRequest(request)) {
    return { ...originalTypes.collection, collection: {} };
  }

  return {};
};

export const getStopParcelCodes = ({ parcel = [] }) =>
  parcel.reduce(
    (acc, item) =>
      !!item && item.parcelCode ? [...acc, item.parcelCode] : acc,
    []
  );

export const getMergeToParcelList = (selectedStops, pickedStop) => [
  ...new Set(
    R.flatten([
      selectedStops.map(getStopParcelCodes),
      getStopParcelCodes(pickedStop),
    ])
  ),
];

export const findStopByStopNumber = (stops, findStopNumber) =>
  stops.find(({ stopNumber }) => stopNumber === findStopNumber);

export const sortByStopNumber = (stops) =>
  stops.sort((a, b) => a.stopNumber - b.stopNumber);

export const getHighestStopNumber = (stops = [], requests = []) =>
  Math.max(
    0,
    ...stops.map(R.path(["stopNumber"])),
    ...requests.map(R.path(["stopNumber"]))
  );

export const getHighestSequenceNumber = (stops) =>
  Math.max(1, ...stops.map(R.path(["sequenceNumber"])));

export const getServiceWindow = ({
  displayServiceWindow,
  serviceStartTime,
  serviceEndTime,
}) =>
  displayServiceWindow ? createTimeRange(serviceStartTime, serviceEndTime) : "";

export const formatDisplayNumber = (displayNumber) =>
  `${displayNumber}`.replace(".", "-");

export const formatDisplayNumberToFloat = (stop) => ({
  ...stop,
  displayNumber: `${stop.displayNumber}`.replace("-", "."),
});
